import { DropdownInput, NativeMountingInputs } from 'types/mounting'
import { StudioSystemType } from 'types/studio/items'
import { BomGeneratorAbstract } from '../bom_generators/BomGeneratorAbstract'
import {
  HorizontalRailPosition,
  PerRowRailBomGeneratorAbstract,
} from '../bom_generators/PerRowRailBomGeneratorAbstract'
import {
  BasicSpacingLayoutAdjusterAbstract,
  SpacingRuleSet,
} from '../layout_adjusters/BasicSpacingLayoutAdjusterAbstract'
import { MountingSystemAbstract } from '../MountingSystemAbstract'
import { CutAndSplicerAbstract } from '../post_processors/CutAndSplicerAbstract'
import { RoofHookAdderAbstract } from '../post_processors/RoofHookAdderAbstract'
import {
  CompatibilityParameters,
  Component,
  Direction,
  Fastener,
  Item,
  MountingCalcInput,
  MountingCalcResult,
  MountingComponentType,
  Panel,
  PanelBlock,
  QuantityPerProduct,
  RailComponent,
  RailType,
} from '../types'

import { getPanelOrientationsInSystem } from '../railing'
import { getQuantityOfProducts } from '../utils'

const spacingRuleSet: SpacingRuleSet = {
  interPanel: { horizontalSpacing: 18, verticalSpacing: 18 },
}
class FastensolSpacingAdjuster extends BasicSpacingLayoutAdjusterAbstract {
  spacingRuleSet = spacingRuleSet
}

const railOverhang = 50

export const FASTENSOL_MOUNTING_ID = 'fastensol' as const

export const FASTENSOL_RAIL_VARIABLE_NAME = 'railColour' as const

// Single source of truth for the Fastensol rail SKUs the Studio dropdown
// offers. Display order matches the dropdown so the smart-default picker
// (FastensolDefaults.ts) breaks intra-supplier ties the same way.
export const FASTENSOL_RAIL_CODES = ['F-MR-S3550', 'F-MR-S', 'F-MR-S-3600', 'FASTENSOL F-MR-B', 'F-MR-B-3550'] as const

export type FastensolRailCode = (typeof FASTENSOL_RAIL_CODES)[number]

// Display labels for the Fastensol Railing dropdown. Keyed by `FastensolRailCode`
// so adding/removing a SKU in `FASTENSOL_RAIL_CODES` becomes a TypeScript error
// here until the label map is updated in lock-step.
export const FASTENSOL_RAIL_LABELS: Record<FastensolRailCode, string> = {
  'F-MR-S3550': 'Silver Mounting Rail 3.55m',
  'F-MR-S': 'Silver Mounting Rail 3.3m',
  'F-MR-S-3600': 'Silver Mounting Rail 3.6m',
  'FASTENSOL F-MR-B': 'Black Mounting Rail 3.3m',
  'F-MR-B-3550': 'Black Mounting Rail 3.55m',
}

export const FASTENSOL_END_CAP_VARIABLE_NAME = 'endCap' as const

export const FASTENSOL_END_CAP_CODES = ['F-REC-S', 'FASTENSOL F-REC-B'] as const

export type FastensolEndCapCode = (typeof FASTENSOL_END_CAP_CODES)[number]

export const FASTENSOL_END_CAP_LABELS: Record<FastensolEndCapCode, string> = {
  'F-REC-S': 'Rail End Cap Silver',
  'FASTENSOL F-REC-B': 'Rail End Cap Black',
}

export const FASTENSOL_END_CLAMP_VARIABLE_NAME = 'endClamp' as const

export const FASTENSOL_END_CLAMP_CODES = ['F-BEC', 'F-UNIVERSAL-CLAMP', 'FASTENSOL F-UC-B', 'F-UC-S'] as const

export type FastensolEndClampCode = (typeof FASTENSOL_END_CLAMP_CODES)[number]

export const FASTENSOL_END_CLAMP_LABELS: Record<FastensolEndClampCode, string> = {
  'F-BEC': 'Black End Clamp',
  'F-UNIVERSAL-CLAMP': 'Black Universal Clamp for 30-40mm Panel Thickness', //BOTH MID and End Clamp
  'FASTENSOL F-UC-B': 'Black Universal Clamp for 30-46mm Panel Thickness',
  'F-UC-S': 'Silver Universal Clamp for 30-46mm Panel Thickness',
}

export const FASTENSOL_MID_CLAMP_VARIABLE_NAME = 'midClamp' as const

export const FASTENSOL_MID_CLAMP_CODES = [
  'F-BMC',
  'F-UNIVERSAL-CLAMP',
  'FASTENSOL F-UC-B',
  'FASTENSOL F-SMC',
  'F-UC-S',
] as const

export type FastensolMidClampCode = (typeof FASTENSOL_MID_CLAMP_CODES)[number]

export const FASTENSOL_MID_CLAMP_LABELS: Record<FastensolMidClampCode, string> = {
  'F-BMC': 'Black Mid Clamp',
  'F-UNIVERSAL-CLAMP': 'Black Universal Clamp for 30-40mm Panel Thickness',
  'FASTENSOL F-UC-B': 'Black Universal Clamp for 30-46mm Panel Thickness',
  'FASTENSOL F-SMC': 'Silver Mid Clamp for 30mm',
  'F-UC-S': 'Silver Universal Clamp for 30-46mm Panel Thickness',
}

export const FASTENSOL_SPLICE_VARIABLE_NAME = 'railSplice' as const

export const FASTENSOL_SPLICE_CODES = ['F-RC-S', 'FASTENSOL F-RC-B'] as const

export type FastensolSpliceCode = (typeof FASTENSOL_SPLICE_CODES)[number]

export const FASTENSOL_SPLICE_LABELS: Record<FastensolSpliceCode, string> = {
  'F-RC-S': 'Silver Splice',
  'FASTENSOL F-RC-B': 'Black Splice',
}

export const FASTENSOL_ROOF_HOOK_VARIABLE_NAME = 'roofHook' as const
export const FASTENSOL_ROOF_HOOK_PORTRAIT_VARIABLE_NAME = 'roofHookPortrait' as const
export const FASTENSOL_ROOF_HOOK_LANDSCAPE_VARIABLE_NAME = 'roofHookLandscape' as const

export const FASTENSOL_PORTRAIT_ROOF_HOOK_CODES = ['F-TH01', 'F-TH02', 'FASTENSOL F-TH05', 'FASTENSOL F-TH04'] as const
export const FASTENSOL_LANDSCAPE_ROOF_HOOK_CODES = ['F-TH02A', 'F-TH06'] as const

export const FASTENSOL_TILE_SLATE_PORTRAIT_ROOF_HOOK_CODES = ['FASTENSOL F-TH04', 'F-TH02'] as const
export const FASTENSOL_TILE_SLATE_LANDSCAPE_ROOF_HOOK_CODE = 'F-TH02A' as const
export const FASTENSOL_TILE_SLATE_PORTRAIT_ROOF_HOOK_DEFAULT: (typeof FASTENSOL_TILE_SLATE_PORTRAIT_ROOF_HOOK_CODES)[number] =
  'FASTENSOL F-TH04'

export type FastensolPortraitRoofHookCode = (typeof FASTENSOL_PORTRAIT_ROOF_HOOK_CODES)[number]
export type FastensolLandscapeRoofHookCode = (typeof FASTENSOL_LANDSCAPE_ROOF_HOOK_CODES)[number]

export const FASTENSOL_PORTRAIT_ROOF_HOOK_DEFAULT: FastensolPortraitRoofHookCode = 'F-TH01'
export const FASTENSOL_LANDSCAPE_ROOF_HOOK_DEFAULT: FastensolLandscapeRoofHookCode = 'F-TH02A'

export const FASTENSOL_PORTRAIT_ROOF_HOOK_LABELS: Record<FastensolPortraitRoofHookCode, string> = {
  'F-TH02': 'F-TH02 Plain Tile Roof Hook Portrait Including 2PCS Wood Screw',
  'F-TH01': 'F-TH01 Pantile Roof Hook Portrait Including 3PCS Wood Screw',
  'FASTENSOL F-TH05': 'F-TH05 Adjustable Tile Roof Hook Portrait Including 3PCS Wood Screw',
  'FASTENSOL F-TH04': 'F-TH04 Slate Roof Hook Portrait Including 3PCS Wood Screw',
}

export const FASTENSOL_LANDSCAPE_ROOF_HOOK_LABELS: Record<FastensolLandscapeRoofHookCode, string> = {
  'F-TH02A': 'F-TH02A Plain Tile Roof Hook Landscape Including 2PCS Wood Screw',
  'F-TH06': 'F-TH06 Pantile Roof Hook Landscape Including 3PCS Wood Screw',
}

const portraitRoofHookCodeSet = new Set<string>(FASTENSOL_PORTRAIT_ROOF_HOOK_CODES)
const landscapeRoofHookCodeSet = new Set<string>(FASTENSOL_LANDSCAPE_ROOF_HOOK_CODES)

export function isFastensolPortraitRoofHookCode(code: string): code is FastensolPortraitRoofHookCode {
  return portraitRoofHookCodeSet.has(code)
}

export function isFastensolLandscapeRoofHookCode(code: string): code is FastensolLandscapeRoofHookCode {
  return landscapeRoofHookCodeSet.has(code)
}

export function getFastensolRoofHookCodesForRoofType(
  orientation: 'portrait' | 'landscape',
  roofTypeName: string | null | undefined
): readonly string[] {
  if (roofTypeName === 'Tile Slate') {
    return orientation === 'portrait'
      ? FASTENSOL_TILE_SLATE_PORTRAIT_ROOF_HOOK_CODES
      : [FASTENSOL_TILE_SLATE_LANDSCAPE_ROOF_HOOK_CODE]
  }
  return orientation === 'portrait' ? FASTENSOL_PORTRAIT_ROOF_HOOK_CODES : FASTENSOL_LANDSCAPE_ROOF_HOOK_CODES
}

export function getFastensolRoofHookCode(input: MountingCalcInput, panelBlock: PanelBlock): string {
  if (panelBlock.orientation === 'portrait') {
    if (input.options.roofHookPortrait) return input.options.roofHookPortrait
    if (input.options.roofHook && isFastensolPortraitRoofHookCode(input.options.roofHook)) {
      return input.options.roofHook
    }
    return FASTENSOL_PORTRAIT_ROOF_HOOK_DEFAULT
  }
  if (input.options.roofHookLandscape) return input.options.roofHookLandscape
  if (input.options.roofHook && isFastensolLandscapeRoofHookCode(input.options.roofHook)) {
    return input.options.roofHook
  }
  return FASTENSOL_LANDSCAPE_ROOF_HOOK_DEFAULT
}

function buildPortraitRoofHookOptions(codes: readonly string[]) {
  return codes.map((code) => ({
    label: FASTENSOL_PORTRAIT_ROOF_HOOK_LABELS[code as FastensolPortraitRoofHookCode],
    value: code,
  }))
}

function buildLandscapeRoofHookOptions(codes: readonly string[]) {
  return codes.map((code) => ({
    label: FASTENSOL_LANDSCAPE_ROOF_HOOK_LABELS[code as FastensolLandscapeRoofHookCode],
    value: code,
  }))
}

export function getFastensolRoofHookInputs(
  system: StudioSystemType,
  roofTypeName?: string | null
): NativeMountingInputs {
  const orientations = getPanelOrientationsInSystem(system)
  const dropdownInputs: DropdownInput[] = []
  const isTileSlate = roofTypeName === 'Tile Slate'
  const portraitCodes = getFastensolRoofHookCodesForRoofType('portrait', roofTypeName)
  const landscapeCodes = getFastensolRoofHookCodesForRoofType('landscape', roofTypeName)
  const portraitDefault = isTileSlate
    ? FASTENSOL_TILE_SLATE_PORTRAIT_ROOF_HOOK_DEFAULT
    : FASTENSOL_PORTRAIT_ROOF_HOOK_DEFAULT
  const landscapeDefault = FASTENSOL_LANDSCAPE_ROOF_HOOK_DEFAULT

  if (orientations.portrait && orientations.landscape) {
    dropdownInputs.push(
      {
        label: 'Portrait Roof Hooks',
        variableName: FASTENSOL_ROOF_HOOK_PORTRAIT_VARIABLE_NAME,
        default: portraitDefault,
        options: buildPortraitRoofHookOptions(portraitCodes),
      },
      {
        label: 'Landscape Roof Hooks',
        variableName: FASTENSOL_ROOF_HOOK_LANDSCAPE_VARIABLE_NAME,
        default: landscapeDefault,
        options: buildLandscapeRoofHookOptions(landscapeCodes),
      }
    )
  } else if (orientations.portrait) {
    dropdownInputs.push({
      label: 'Roof Hook',
      variableName: FASTENSOL_ROOF_HOOK_VARIABLE_NAME,
      default: portraitDefault,
      options: buildPortraitRoofHookOptions(portraitCodes),
    })
  } else if (orientations.landscape) {
    dropdownInputs.push({
      label: 'Roof Hook',
      variableName: FASTENSOL_ROOF_HOOK_VARIABLE_NAME,
      default: landscapeDefault,
      options: buildLandscapeRoofHookOptions(landscapeCodes),
    })
  }

  return { dropdownInputs }
}

const FASTENSOL_RAIL_LENGTHS: Record<FastensolRailCode, number> = {
  'F-MR-S3550': 3550,
  'F-MR-S': 3300,
  'F-MR-S-3600': 3600,
  'FASTENSOL F-MR-B': 3300,
  'F-MR-B-3550': 3550,
}

const DEFAULT_RAIL_LENGTH = 3300

class FastensolBomGenerator extends BomGeneratorAbstract {
  async generateBom(result: MountingCalcResult): Promise<MountingCalcResult> {
    const starterBom = await this.generateStarterBom(result)
    return this.postProcess(starterBom)
  }

  generateStarterBom(result: MountingCalcResult): Promise<MountingCalcResult> {
    if (this.block.orientation === 'portrait') {
      return this.generateBomFrom(FastensolPortraitBomGenerator, result)
    } else {
      return this.generateBomFrom(FastensolLandscapeBomGenerator, result)
    }
  }

  postProcess(result: MountingCalcResult): MountingCalcResult {
    const resultWithRoofHooks = new FastensolRoofHookAdder(this.input, this.block).process(result)
    const resultWithSplices = new FastensolRailSplicer(this.input, this.block).process(resultWithRoofHooks)
    return resultWithSplices
  }
}

export class Fastensol extends MountingSystemAbstract {
  layoutAdjusterClass = FastensolSpacingAdjuster
  bomGeneratorClass = FastensolBomGenerator

  getFastener(): Fastener {
    const firstBlock = this.input.panelBlocks[0]
    const orientation = firstBlock.moduleGrid.moduleLayout()
    const panelBlockForHook = { orientation } as PanelBlock
    return chooseFastener(chooseHook(this.input, panelBlockForHook, chooseRail(this.input, 'horizontal')))
  }

  getCompatibilityParameters(): CompatibilityParameters {
    return {
      integrations: ['City Plumbing'],
      slopeRange: [0, 60],
      roofTypes: ['Tile Clay', 'Tile Concrete', 'Tile Slate'],
      roofTypeRequired: true,
    }
  }
}

class FastensolPortraitBomGenerator extends PerRowRailBomGeneratorAbstract {
  chooseRail() {
    return chooseRail(this.input, 'horizontal')
  }

  chooseMidClamp() {
    return chooseMidClamp(this.input)
  }

  chooseEndClamp() {
    return chooseEndClamp(this.input)
  }

  chooseEndCap() {
    return chooseEndCap(this.input)
  }

  getRailPositions(panel: Panel): HorizontalRailPosition[] {
    const distanceFromRailToPanelEdge = (panel.height * 0.6) / 2

    return [
      {
        top: distanceFromRailToPanelEdge,
        left: -railOverhang,
        right: railOverhang,
      },
      {
        top: panel.height - distanceFromRailToPanelEdge,
        left: -railOverhang,
        right: railOverhang,
      },
    ]
  }
}

class FastensolLandscapeBomGenerator extends PerRowRailBomGeneratorAbstract {
  chooseRail() {
    return chooseRail(this.input, 'horizontal')
  }

  chooseMidClamp() {
    return chooseMidClamp(this.input)
  }

  chooseEndClamp() {
    return chooseEndClamp(this.input)
  }

  chooseEndCap() {
    return chooseEndCap(this.input)
  }

  getRailPositions(panel: Panel): HorizontalRailPosition[] {
    const distanceFromRailToPanelEdge = (panel.width * 0.6) / 2

    return [
      {
        top: distanceFromRailToPanelEdge,
        left: -railOverhang,
        right: railOverhang,
      },
      {
        top: panel.width - distanceFromRailToPanelEdge,
        left: -railOverhang,
        right: railOverhang,
      },
    ]
  }
}

function chooseRail(input: MountingCalcInput, direction: Direction): RailComponent {
  const componentCode = input.options.railColour as string
  return {
    type: MountingComponentType.RAIL,
    railType: 'continuous',
    direction,
    name: componentCode,
    length: 0,
    top: 0,
    left: 0,
  }
}

function chooseHook(input: MountingCalcInput, panelBlock: PanelBlock, rail: RailComponent) {
  const componentCode = getFastensolRoofHookCode(input, panelBlock)
  return [
    {
      name: componentCode,
      components: [
        {
          name: componentCode,
          type: MountingComponentType.ROOF_HOOK,
          left: -10,
          top: -10,
        },
      ],
    },
  ]
}

function chooseEndCap(input: MountingCalcInput): Item {
  const componentCode = input.options.endCap as string
  return {
    name: componentCode,
    components: [
      {
        name: componentCode,
        type: MountingComponentType.OTHER,
        left: -18.1,
        top: -24.8,
      },
    ],
  }
}

function chooseEndClamp(input: MountingCalcInput): Item {
  const componentCode = input.options.endClamp as string
  return {
    name: componentCode,
    components: [
      {
        name: componentCode,
        type: MountingComponentType.CLAMP,
        left: -10,
        top: -10,
      },
    ],
  }
}

function chooseMidClamp(input: MountingCalcInput): Item {
  const componentCode = input.options.midClamp as string
  return {
    name: componentCode,
    components: [
      {
        name: componentCode,
        type: MountingComponentType.CLAMP,
        left: -10,
        top: -10,
      },
    ],
  }
}

const fastensolFastenersNeededPerHook: QuantityPerProduct = {
  '2': ['F-TH02', 'F-TH02A'],
  '3': ['F-TH01', 'F-TH06', 'FASTENSOL F-TH05', 'FASTENSOL F-TH04'],
}

function chooseFastener(hook: Item[]): Fastener {
  const hookName = hook[0].name
  const fastenersQty = getQuantityOfProducts(fastensolFastenersNeededPerHook, hookName) as number
  return {
    includedInRoofHookProduct: true,
    name: '',
    qtyPerRoofHook: fastenersQty,
    length: 80,
    diameter: 6.3,
    components: [],
  }
}

class FastensolRoofHookAdder extends RoofHookAdderAbstract {
  spacingRuleSet = spacingRuleSet
  railOverhang = railOverhang

  getRailType(input: MountingCalcInput): RailType {
    return 'continuous'
  }

  chooseHook(input: MountingCalcInput, panelBlock: PanelBlock, rail: RailComponent) {
    return chooseHook(input, panelBlock, rail)
  }

  chooseFastener(hooks) {
    return chooseFastener(hooks)
  }
}

class FastensolRailSplicer extends CutAndSplicerAbstract {
  isTarget(component: Component): boolean {
    return component?.type === MountingComponentType.RAIL
  }

  chooseSplice(railName: string, input: MountingCalcInput): Item[] {
    const componentCode = (input.options.railSplice as string) || 'F-RC-S'
    return [
      {
        name: componentCode,
        components: [
          {
            type: MountingComponentType.SPLICE,
            name: componentCode,
            top: 0,
            left: 0,
          },
        ],
      },
    ]
  }

  getFullLength(railName: string): number {
    return FASTENSOL_RAIL_LENGTHS[railName] ?? DEFAULT_RAIL_LENGTH
  }
}
